import { mapValues, pickBy } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import { computed } from 'vue'
import * as Sentry from '@sentry/nuxt'
import {
  SortName,
  type Product,
  type ProductSearchQuery,
} from '@scayle/storefront-nuxt'
import { applyOdoscopeCookies } from '@goldner-group/storefront-odoscope'
import type {
  OdoscopeEvent,
  OdoscopeListingRpcResult,
  OdoscopeProductQuery,
  OdoscopeTokenSlot,
} from '@goldner-group/storefront-odoscope/types'
import useParameterMemory from '../../composables/useParameterMemory'

import useSorting from '../../composables/useSorting'
import { INCLUDED_FILTERS } from '../../constants/filters'
import {
  createOdoscopeRouteStateKey,
  ODOSCOPE_ROUTE_SLICE_STATE_KEY,
} from '../../utils/odoscopeStateKey'
import {
  buildPlpEmptyReport,
  PLP_EMPTY_REASON,
  type PlpEmptyReason,
} from '../../utils/plpObservability'
import { shapeProductCardPayloads } from '../../utils/productPayload'
import { createKeyedSingleFlight } from '../../utils/keyedSingleFlight'
import { PRODUCTS_PER_PAGE } from '~/constants/product'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { useCurrentShop, useRpc } from '#storefront/composables'
import { useState, useRoute } from '#app/composables'

type OdoscopeRouteSlice = {
  products: Product[]
  productsFetching: boolean
  error: boolean
  resultsCount: number
  // Whether a fetch has *completed* (settled with a real Odoscope response) for
  // this route slice. Distinguishes a slice that is empty because it was never
  // fetched (must reconcile — ECOM-2692) from one that genuinely fetched empty
  // (e.g. no results for an applied filter). Only the completed-RPC path sets
  // this true; the missing-category and stale-route bails deliberately leave it
  // false (see `fetchProducts`).
  fetched: boolean
}

export default () => {
  const runProductFetch = createKeyedSingleFlight<undefined>()
  const productsPerPage = PRODUCTS_PER_PAGE

  const log = useLog('useOdoscope')

  const route = useRoute()
  const currentShop = useCurrentShop()
  const { params: utmParams } = useParameterMemory('utm_')

  const { selectedSort } = useSorting()
  const { appliedFilter } = useAppliedFilters()

  const {
    trackOdoscopeTimeout,
    trackOdoscopeError,
    trackOdoscopeControlGroup,
    trackOdoscopeEmptyResponse,
    trackOdoscopeSuccess,
  } = useTrackingEvents(true)

  const plpPath = computed(() => {
    if (Array.isArray(route.params.slug)) {
      return `/${route.params.slug.join('/')}`
    }
    return `/${route.params.slug}`
  })

  const routeStateKey = computed(() =>
    createOdoscopeRouteStateKey(route.fullPath),
  )
  const term = computed(() => route.query.term || '')

  const categoriesForListing = useCategories(
    {
      params: computed(() => ({
        path: plpPath.value,
      })),
      options: { immediate: false, watch: [] },
    },
    computed(() => `useCategories-useOdoscope-plp-${routeStateKey.value}`),
  )

  const filtersForListing = useFilters(
    {
      params: computed(() => ({
        where: {
          term: term.value.toString(),
        },
        category: plpPath.value,
        includedFilters: INCLUDED_FILTERS,
      })),
      options: { immediate: false, watch: [] },
    },
    computed(() => `useFilters-useOdoscope-plp-${routeStateKey.value}`),
  )

  const filters = computed(() => filtersForListing.data.value)

  const pageQuery = computed(() =>
    Array.isArray(route.query.page) ? route.query.page[0] : route.query.page,
  )

  // +----------+
  // | products |
  // +----------+
  const sliceByRoute = useState<Record<string, OdoscopeRouteSlice>>(
    ODOSCOPE_ROUTE_SLICE_STATE_KEY,
    () => ({}),
  )

  const ensureSlice = (key: string): OdoscopeRouteSlice => {
    const map = sliceByRoute.value
    if (!map[key]) {
      map[key] = {
        products: [],
        productsFetching: false,
        error: false,
        resultsCount: 0,
        fetched: false,
      }
    }
    return map[key]!
  }

  const products = computed(() => ensureSlice(routeStateKey.value).products)

  const productsFetching = computed(
    () => ensureSlice(routeStateKey.value).productsFetching,
  )

  const error = computed(() => ensureSlice(routeStateKey.value).error)

  // True once the current route's slice has *completed* a fetch. Reconciliation
  // (usePlpNavigationReconcile) reads this to tell an enabled-but-never-fetched
  // slice (which must self-heal) from one that genuinely fetched empty
  // (ECOM-2692 / ECOM-2634).
  const isReady = computed(() => ensureSlice(routeStateKey.value).fetched)

  // +------------+
  // | pagination |
  // +------------+
  const resultsCount = computed(
    () => ensureSlice(routeStateKey.value).resultsCount,
  )
  const isFirstPage = computed(() => pagination.value?.page === 1)
  const page = computed(() => (pageQuery.value ? parseInt(pageQuery.value) : 1))

  const pageCount = computed(() =>
    Math.ceil(resultsCount.value / productsPerPage),
  )

  const pagination = computed(() =>
    resultsCount.value > 1
      ? {
          page: page.value,
          first: 1,
          last: pageCount.value,
        }
      : undefined,
  )

  // +----------+
  // | odoscope |
  // +----------+
  const getTrackingLabel = (event?: OdoscopeEvent) =>
    event?.label || event?.tokenSlot || 'product'

  const handleOdoscopeEventTracking = (event?: OdoscopeEvent) => {
    if (!event) {
      return
    }

    const label = getTrackingLabel(event)

    if (event.action === 'control') {
      trackOdoscopeControlGroup(label)
      return
    }

    if (event.action === 'empty') {
      trackOdoscopeEmptyResponse(label)
      return
    }

    if (event.action === 'timeout') {
      trackOdoscopeTimeout(label)
      return
    }

    if (event.action === 'error') {
      trackOdoscopeError(label)
      return
    }

    trackOdoscopeSuccess(label)
  }

  const getOdoscopeProducts = async (
    query: OdoscopeProductQuery,
    fallbackParams: Record<string, any>,
    routeForFetch: string,
  ): Promise<OdoscopeListingRpcResult | undefined> =>
    log.time('Fetching odoscope products', async () => {
      const {
        data,
        error: rpcError,
        refresh,
      } = useRpc(
        'getOdoscopeProductListing',
        `getOdoscopeProductListing-${routeForFetch}`,
        {
          query,
          fallback: {
            params: fallbackParams,
            page: page.value,
            perPage: PRODUCTS_PER_PAGE,
          },
        },
        { immediate: false, lazy: false, server: false } as any,
      )

      await refresh()

      if (rpcError.value) {
        throw rpcError.value
      }

      applyOdoscopeCookies(data.value?.oscCookie)
      return data.value || undefined
    })

  const getFiltersByValue = (rawFilters: {
    [x: string]: string | number | (string | number | null)[]
  }) => {
    return mapValues(rawFilters, (value, key) => {
      if (!Array.isArray(value)) {
        value = [value]
      }
      value = value.map((s) => (typeof s === 'string' ? parseInt(s) : s))
      const allFilters = filters.value?.filters.find((f) => f.slug === key)
      return allFilters?.values
        ?.filter((s: any) => value?.includes(s.id))
        .map((s: any) => {
          // For sizes we need to extract the correct value
          if (key === 'sizeCategorySize') {
            return s.name.replace('|', '')
          }
          return s.value
        })
    })
  }

  /**
   * Transforms the filters (product where Condition) to the format used by odoscope
   * @param filters - The filters to transform
   * @returns The transformed filters
   */
  const transformFiltersToOdoscope = (filters: ProductSearchQuery) => {
    const transformed = transformWhereConditionToMap(filters)

    // Odoscope uses `isSale` instead of `sale`
    if ('sale' in transformed) {
      transformed.isSale = transformed.sale === 'true'
      delete transformed.sale
    }

    const { minPrice, maxPrice, isNew, isSale, ...other } = transformed

    // We need to resolve the value IDs to the actual values
    const transformedFilters = getFiltersByValue(other)

    if (transformedFilters.sizeCategorySize) {
      transformedFilters.sizeCategory_size = transformedFilters.sizeCategorySize
      delete transformedFilters.sizeCategorySize
    }

    delete transformed.sizeCategorySize

    return pickBy(
      {
        ...transformed,
        ...transformedFilters,
      },
      Boolean,
    )
  }

  // Observability for the recurring "empty PLP" class of bug (ECOM-2634).
  // Reports to Sentry with enough context to correlate against odoscope's own
  // request logs (shopId, category, route) without relying on a manual repro.
  // Shares the reason set + payload shape with the reconciliation signal via
  // buildPlpEmptyReport (ECOM-2692) so all empty-PLP reasons report uniformly.
  const reportEmptyPlpToSentry = (
    reason: PlpEmptyReason,
    extra: Record<string, unknown>,
  ) => {
    const report = buildPlpEmptyReport(reason, {
      plpPath: plpPath.value,
      routeFullPath: route.fullPath,
      shopId: currentShop.value?.shopId,
      categoriesStatus: categoriesForListing.status.value,
      filtersStatus: filtersForListing.status.value,
      ...extra,
    })
    Sentry.captureMessage(report.message, {
      level: report.level,
      tags: report.tags,
      extra: report.extra,
    })
  }

  const fetchProducts = async (
    fallbackParams: Record<string, any>,
    tokenSlot?: OdoscopeTokenSlot,
    // ECOM-2669: the caller (useNewPlp) resolves the active category before
    // deciding the product source. When it passes the resolved id in, skip this
    // composable's own (separately-keyed) category fetch so it doesn't run
    // serially after the caller's and add a round-trip to every Odoscope PLP.
    categoryIdOverride?: number,
  ) => {
    const routeForFetch = routeStateKey.value
    return runProductFetch(routeForFetch, async (): Promise<undefined> => {
      const slice = ensureSlice(routeForFetch)
      slice.productsFetching = true
      slice.error = false

      try {
        // `filtersForListing`/`categoriesForListing` use a route-reactive
        // fetching key. On client navigation `status` can read `'success'` from a
        // sibling cache entry while `data` for the current route is still absent,
        // so we must also refresh when the data we depend on is missing — not
        // only when the status is non-success.
        const categoriesDataMissing =
          !categoriesForListing.data.value?.activeNode
        const filtersDataMissing = !filtersForListing.data.value

        const needFilters =
          filtersForListing.status.value !== 'success' || filtersDataMissing
        const needCategories =
          categoryIdOverride === undefined &&
          (categoriesForListing.status.value !== 'success' ||
            categoriesDataMissing)

        const pendingRefreshes: Promise<unknown>[] = []
        if (needFilters) {
          pendingRefreshes.push(filtersForListing.refresh())
        }
        if (needCategories) {
          pendingRefreshes.push(categoriesForListing.refresh())
        }
        await Promise.all(pendingRefreshes)

        if (routeStateKey.value !== routeForFetch) {
          return
        }

        const categoryId =
          categoryIdOverride ?? categoriesForListing.data.value?.activeNode?.id

        // Without a resolved category we cannot build a meaningful odoscope query.
        // Sending `categoryId: undefined` makes odoscope return an empty result
        // set, which previously surfaced as an empty PLP (ECOM-2634). Flag an
        // error instead so the caller falls back to the path-based storefront
        // listing, which does not depend on this reactive fetching key.
        if (!categoryId) {
          slice.error = true
          reportEmptyPlpToSentry(PLP_EMPTY_REASON.missingCategory, {
            hasActiveNode: Boolean(categoriesForListing.data.value?.activeNode),
          })
          return
        }

        const transformedAppliedFilters = transformFiltersToOdoscope(
          appliedFilter.value,
        )

        const query = {
          ...utmParams.value,
          categoryId,
          limit: PRODUCTS_PER_PAGE,
          offset: (page.value - 1) * PRODUCTS_PER_PAGE,
          sorting: selectedSort.value.name || SortName.TOPSELLER,
          referrer: import.meta.client
            ? sessionStorage?.getItem('referrer')
            : null,
          filters: transformedAppliedFilters,
          tokenSlot,
        }

        log.debug(
          `Fetching product-IDs from odoscope by category-ID: ${categoryId}`,
          query,
        )

        const response = await getOdoscopeProducts(
          query as any,
          fallbackParams,
          routeForFetch,
        )

        if (routeStateKey.value !== routeForFetch) {
          return
        }

        handleOdoscopeEventTracking(response?.odoscopeEvent)

        slice.resultsCount = response?.total || 0
        slice.products = shapeProductCardPayloads(response?.products || [])
        // Real response settled — mark fetched (see the `fetched` field doc). The
        // `!categoryId` and stale-route bails return before this point, leaving it
        // false on purpose.
        slice.fetched = true

        if (!slice.products?.length && isEmpty(query.filters)) {
          slice.error = true
          // A/B control group legitimately returns no odoscope products and
          // falls back to the storefront listing — don't report that as a bug.
          if (
            !response?.fallbackUsed &&
            response?.odoscopeEvent?.action !== 'control'
          ) {
            reportEmptyPlpToSentry(PLP_EMPTY_REASON.emptyOdoscopeResponse, {
              categoryId,
              total: response?.total ?? 0,
            })
          }
        }
      } catch (err) {
        if (routeStateKey.value === routeForFetch) {
          handleError(err, routeForFetch, tokenSlot)
        }
      } finally {
        ensureSlice(routeForFetch).productsFetching = false
      }
    })
  }

  const handleError = (
    _err: unknown,
    routeKey: string,
    tokenSlot?: OdoscopeTokenSlot,
  ) => {
    trackOdoscopeError(tokenSlot || 'product')
    ensureSlice(routeKey).error = true
  }

  return {
    fetchProducts,
    error,
    filters,
    isReady,

    products,
    resultsCount,
    productsPerPage,
    productsFetching,

    page,
    pagination,
    pageCount,
    isFirstPage,
  }
}
